import { useState } from "react";
import { format } from "date-fns";
import {
  Button,
  Typography,
  Space,
  Popconfirm,
  message,
} from "antd";
import { ArrowRightOutlined, ReloadOutlined } from "@ant-design/icons";
import { StatusPill, CopyableText, TrackingLink } from "./Atoms";
import { PartnerBadge } from "./Atoms";
import AttemptBadge from "./AttemptBadge";
import RaiseDeliveryIssue from "./RaiseDeliveryIssue";
import useDeliveryStore from "../../store/DeliveryStore";
import {
  canCancelDeliveryAttempt,
  canMarkAttemptDelivered,
  formatDateDifference,
} from "../../helpers/Utils";

const { Text } = Typography;

// ─── Single field block inside an attempt card ────────────────────────────────
function AttemptField({ label, children }) {
  return (
    <div style={{ marginBottom: 24, maxWidth: 240 }}>
      <Text
        style={{
          display: "block",
          fontSize: 10,
          fontWeight: 700,
          color: "#94A3B8",
          textTransform: "uppercase",
          letterSpacing: "0.5px",
          marginBottom: 4,
        }}
      >
        {label}
      </Text>
      {children}
    </div>
  );
}

// ─── Connector arrow between attempt cards ────────────────────────────────────
function Connector() {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        paddingTop: 54, // vertically center against card header
        flexShrink: 0,
        width: 32,
      }}
    >
      <div
        style={{
          width: "100%",
          height: 2,
          background: "#E2E8F0",
          borderRadius: 1,
          position: "relative",
        }}
      >
        <ArrowRightOutlined
          style={{
            position: "absolute",
            right: -6,
            top: -6,
            fontSize: 10,
            color: "#E2E8F0",
          }}
        />
      </div>
    </div>
  );
}

// ─── Individual attempt card ──────────────────────────────────────────────────
function AttemptCard({ attempt, isLatest, onCancel, onMarkDelivered, placedOrderId, outlet }) {
  const isCancellable = canCancelDeliveryAttempt(attempt.status);
  const canMarkDelivered =
    attempt.delivery_partner === "manual" &&
    canMarkAttemptDelivered(attempt.status);

  const handleCancel = async () => {
    try {
      await onCancel(attempt.task_id, attempt.delivery_partner);
      message.success(`Attempt ${attempt.attempt_no} Cancelled`);
    } catch {
      message.error(`Failed to cancel attempt ${attempt.attempt_no}`);
    }
  };

  const handleDelivered = async () => {
    try {
      await onMarkDelivered(attempt);
      message.success(`Attempt ${attempt.attempt_no} Delivered`);
    } catch {
      message.error(`Failed to mark attempt ${attempt.attempt_no} delivered`);
    }
  };

  return (
    <div
      style={{
        flexShrink: 0,
        background: "#fff",
        border: "1px solid #E2E8F0",
        borderRadius: 12,
        overflow: "hidden",
        boxShadow: "none",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Card header */}
      <div
        style={{
          padding: "10px 12px 9px",
          borderBottom: "1px solid #F1F5F9",
          background: "#FAFAFA",
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 24,
        }}
      >
        <Space size={16}>
          <AttemptBadge attemptNo={attempt.attempt_no} />
          <StatusPill status={attempt.status} />
          <PartnerBadge partner={attempt.delivery_partner} />
          {isLatest && (
            <span
              style={{
                fontSize: 10,
                fontWeight: 700,
                color: "#fff",
                background: "#0F172A",
                borderRadius: 4,
                padding: "1px 6px",
                letterSpacing: "0.2px",
              }}
            >
              Final
            </span>
          )}
        </Space>
      </div>

      {/* Card body */}
      <div style={{ padding: "10px 12px 8px", flex: 1 }}>
        <AttemptField label="Created At">
          <Space>
            <Text>
              {format(new Date(attempt.created_at), "dd/MM/yy, h:mm:ss a")}
            </Text>
            <span
              style={{
                background: "#fffbeb",
                border: "1px solid rgb(253, 230, 138)",
                borderRadius: 20,
                padding: "3px 10px",
                color: "#d97706",
              }}
            >
              {formatDateDifference(new Date(), new Date(attempt.created_at))}
            </span>
          </Space>
        </AttemptField>

        <AttemptField label="Task ID">
          <CopyableText text={attempt.task_id} truncate />
        </AttemptField>

        <AttemptField label="Rider">
          {attempt.rider_name ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <CopyableText text={attempt.rider_name} truncate />
              <CopyableText
                style={{
                  color: "#3b82f6",
                }}
                text={attempt.rider_phonenumber}
                truncate
                linkType="phone_number"
              />
            </div>
          ) : (
            <Text style={{ color: "#94A3B8" }}>Unassigned</Text>
          )}
        </AttemptField>

        {attempt.tracking_link && (
          <AttemptField label="Tracking">
            <span onClick={(e) => e.stopPropagation()}>
              <TrackingLink href={attempt.tracking_link} />
            </span>
          </AttemptField>
        )}

        {attempt.cancellation_reason && (
          <AttemptField label="Reason">
            <Text style={{ color: "#DC2626" }}>
              {attempt.cancellation_reason}
            </Text>
          </AttemptField>
        )}
      </div>

      {/* Card footer */}
      <div
        style={{
          padding: "9px 12px",
          borderTop: "1px solid #F1F5F9",
          background: "#FAFAFA",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
        }}
      >
        <Popconfirm
          title={`Cancel Attempt ${attempt.attempt_no}?`}
          description="This will mark the delivery attempt as cancelled."
          onConfirm={handleCancel}
          okText="Yes, cancel"
          cancelText="No"
          okButtonProps={{ danger: true }}
          disabled={!isCancellable}
        >
          <Button
            size="small"
            disabled={!isCancellable}
            style={{
              fontWeight: 600,
              borderRadius: 7,
              height: 26,
              padding: "0 10px",
              boxShadow: "none",
              ...(isCancellable
                ? {
                    color: "#DC2626",
                    borderColor: "#FECACA",
                    background: "#FEF2F2",
                  }
                : {
                    color: "#94A3B8",
                    borderColor: "#E2E8F0",
                    background: "#F9FAFB",
                  }),
            }}
          >
            {attempt.status === "cancelled" ? "Cancelled" : "Cancel"}
          </Button>
        </Popconfirm>

        <Popconfirm
          title={`Mark Attempt ${attempt.attempt_no} delivered?`}
          description="Once marked delivered, it cannot be undone."
          onConfirm={handleDelivered}
          okText="Yes, its delivered"
          cancelText="No"
          okButtonProps={{ danger: true }}
          disabled={!canMarkDelivered}
        >
          <Button
            size="small"
            disabled={!canMarkDelivered}
            style={{
              fontWeight: 600,
              borderRadius: 7,
              height: 26,
              padding: "0 10px",
              boxShadow: "none",
              ...(canMarkDelivered
                ? {
                    color: "#15803d",
                    borderColor: "#9fcbac",
                    background: "#f0fdf4",
                  }
                : {
                    color: "#94A3B8",
                    borderColor: "#E2E8F0",
                    background: "#F9FAFB",
                  }),
            }}
          >
            {attempt.status === "delivered" ? "Delivered" : "Mark Delivered"}
          </Button>
        </Popconfirm>

        <RaiseDeliveryIssue
          attempt={attempt}
          placedOrderId={placedOrderId}
          outlet={outlet}
        />
      </div>
    </div>
  );
}

// ─── Main horizontal timeline ─────────────────────────────────────────────────
export default function AttemptTimeline({ order_id }) {
  const record = useDeliveryStore((s) =>
    s.records.find((r) => r.placed_order_id === order_id),
  );
  const { cancelRecord, fetchRecords, loading, markAttemptDelivered } =
    useDeliveryStore();
  const latestDeliveryId = record.latest_delivery_id;
  const [localAttempts] = useState(record.deliveries);
  const [isAllCancellable] = useState(
    localAttempts.some((attempt) => canCancelDeliveryAttempt(attempt.status)),
  );

  const handleCancel = async (task_id, partner) => {
    await cancelRecord(task_id, "Cancelled by Admin", partner);
  };

  const handleDelivered = async (attempt) => {
    await markAttemptDelivered({
      ...attempt,
      placed_order: record.placed_order_id,
    });
  };

  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          minWidth: 0,
          padding: "6px 0 4px",
          flexWrap: "wrap",
          marginBottom: 16,
          gap: 16,
        }}
      >
        <Text
          style={{
            display: "block",
            fontSize: 12,
            fontWeight: 700,
            color: "#94A3B8",
            textTransform: "uppercase",
            letterSpacing: "0.5px",
          }}
        >
          Attempt History &nbsp;·&nbsp; {localAttempts.length}{" "}
          {localAttempts.length !== 1 ? "attempts" : "attempt"}
        </Text>
        <Button
          icon={<ReloadOutlined />}
          onClick={fetchRecords}
          loading={loading}
          style={{
            borderRadius: 8,
            borderColor: "#E2E8F0",
            color: "#64748B",
            boxShadow: "none",
          }}
        />
        <Popconfirm
          title={`Cancel All Unassigned Attempts?`}
          description="This will mark all the delivery attempts as cancelled."
          onConfirm={() => {
            const cancellableAttempts = localAttempts.filter((attempt) =>
              canCancelDeliveryAttempt(attempt.status),
            );
            cancellableAttempts.forEach((attempt) =>
              handleCancel(attempt.task_id, attempt.delivery_partner),
            );
          }}
          okText="Yes, cancel"
          cancelText="No"
          okButtonProps={{ danger: true }}
          disabled={!isAllCancellable}
        >
          <Button
            size="small"
            disabled={!isAllCancellable}
            style={{
              fontWeight: 600,
              borderRadius: 7,
              height: 26,
              padding: "0 10px",
              boxShadow: "none",
              ...(isAllCancellable
                ? {
                    color: "#DC2626",
                    borderColor: "#FECACA",
                    background: "#FEF2F2",
                  }
                : {
                    color: "#94A3B8",
                    borderColor: "#E2E8F0",
                    background: "#F9FAFB",
                  }),
            }}
          >
            Cancel All
          </Button>
        </Popconfirm>
      </div>

      <div style={{ overflowX: "auto", paddingBottom: 6 }}>
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            minWidth: 0,
            padding: "6px 0 4px",
            flexWrap: "wrap",
            rowGap: 32,
          }}
        >
          {localAttempts.map((attempt, idx) => (
            <div
              key={attempt.id}
              style={{ display: "flex", alignItems: "flex-start" }}
            >
              <AttemptCard
                attempt={attempt}
                isLatest={attempt.id === latestDeliveryId}
                onCancel={handleCancel}
                onMarkDelivered={handleDelivered}
                placedOrderId={record.placed_order_id}
                outlet={record.outlet}
              />
              {idx < localAttempts.length - 1 && <Connector />}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
